// src/utils/sessionEnd.js — WHEN THE SERVER SAYS THE SIGN-IN HAS ENDED.
// ---------------------------------------------------------------------------
// 2026-10-19. Brett: "error when i try open melville droppin report from
// inspec" — an alert "Could not load defects: … 401: Not signed in.", then a
// PIN box, then signed in before he answered it. The window still held his
// name from an old visit, so the report opened and asked for its pins before
// anything checked the sign-in. The server refused them (the alert); the tab's
// own re-check then dropped it to the sign-in screen, where the browser offered
// his saved password and Windows asked for its PIN; and the suite sign-in took
// him straight back in, so the box went away unanswered. "you shouldnt show
// the message then make it disappear as it makes it look like it doesnt have
// good security".
//
// Now one place decides. A 401 on an office request is checked against the
// session — one /api/auth-me, shared by every request that asks at once,
// because a 401 can also be another service refusing (the InSite site sync
// answers "Not signed in." for its own reasons). When the session really has
// ended, the shell is told (SESSION_ENDED_EVENT) and takes the person through
// the suite sign-in behind the "Signing you in" screen; the error is marked
// `sessionEnded`, and from then until a sign-in the screen being left shows
// nothing — no alert (alertFailure), no error toast (Toaster).
import { fetchMe, getCurrentUser } from './user.js';
import { hasContactSession } from './contact-session.js';

export const SESSION_ENDED_EVENT = 'droppin:session-ended';

// Requests whose 401 is about something else: signing in itself, a client or
// subbie's own cookie, a share link, the suite hand-over.
const NOT_THE_SESSION = /^\/api\/(auth-|contact-|public-|sso[/-])/;

let inFlight = null;
let ended = false;

/** fetchMe, shared by every caller that asks while one is on its way. */
export function checkSession(ask = fetchMe) {
  if (!inFlight) inFlight = Promise.resolve().then(ask).finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * A request to `path` came back 401. Has this sign-in ended? True only when
 * the session check says so (or already did); the shell is told once.
 */
export async function sessionHasEnded(path, deps = {}) {
  const {
    contact = hasContactSession,
    cached = getCurrentUser,
    check = checkSession,
    tell = tellShell,
  } = deps;
  if (NOT_THE_SESSION.test(String(path || ''))) return false;
  if (contact()) return false;
  if (ended) return true;
  if (!cached()) return false;
  const me = await check();
  if (!me || !me.signedOut) return false;
  if (!ended) { ended = true; tell(); }
  return true;
}

function tellShell() {
  try { window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT)); } catch (_) { /* no window: nothing to tell */ }
}

/** The shell found the sign-in ended itself (on open, or a tab shown again). */
export function noteSessionEnded() { ended = true; }

/** Signed in again on this page: failures show again. */
export function sessionResumed() { ended = false; }

/** Is the shell taking this page to sign in? */
export function signInEnded() { return ended; }

/** An error that is the sign-in ending, not the screen's own failure. */
export const isSessionEnded = (e) => !!(e && e.sessionEnded);

/**
 * Tell the person something failed — unless it failed because the sign-in has
 * ended: the shell is already taking them to sign in, and an alert over that
 * is the error-then-gone Brett reported. True when it was shown.
 */
export function alertFailure(message, err, show = (m) => window.alert(m)) {
  if (ended || isSessionEnded(err)) return false;
  show(message);
  return true;
}
